import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { usePartnerAuth } from '../contexts/PartnerAuthContext';
import UpcomingMatchesDashboard from '../components/sports/UpcomingMatchesDashboard';

/**
 * Home page component - Onepager Dashboard
 */
const Home: React.FC = () => {
  const { t } = useTranslation(['common']);
  const { isAuthenticated, user, isLoading } = useAuth();
  const { isAuthenticated: isPartnerAuthenticated } = usePartnerAuth();

  // Get the preferred club ID (only available after auth is fully loaded)
  const preferredClubId = !isLoading ? user?.preferredClubId : undefined;

  const isLoggedIn = isAuthenticated || isPartnerAuthenticated;

  return (
    <div className="page-container" style={containerStyle}>
      {/* Hero Section - Only for guests (not logged in) */}
      {!isLoggedIn && (
        <section className="hero-section" style={heroSectionStyle}>
          <div style={heroOverlayStyle}>
            <div style={heroContentStyle}>
              <h1 style={heroTitleStyle}>{t('common:home.hero.title')}</h1>
              <p style={heroSubtitleStyle}>
                {t('common:home.hero.subtitle')}
              </p>
              <p style={heroDescriptionStyle}>
                {t('common:home.hero.description')}
              </p>
              <div style={heroButtonsStyle}>
                <Link to="/matchday-planning-demo" style={primaryButtonStyle}>
                  {t('common:home.hero.tryDemo')}
                </Link>
                <Link to="/login" style={secondaryButtonStyle}>
                  {t('common:home.hero.signIn')} (Microsoft)
                </Link>
                <Link to="/partner/login" style={tertiaryButtonStyle}>
                  {t('common:home.hero.signIn')} (Partner)
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Matches Section - Only for authenticated Verein users (not partners) */}
      {isAuthenticated && !isPartnerAuthenticated && !isLoading && (
        <section style={matchesSectionStyle}>
          <div style={matchesContainerStyle}>
            <UpcomingMatchesDashboard
              daysAhead={1000}
              maxMatches={1000}
              showHeader={true}
              clubId={preferredClubId}
            />
          </div>
        </section>
      )}
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#f8f9fa',
  display: 'flex',
  flexDirection: 'column',
};

// --- Full hero (guests) ---
const heroSectionStyle: React.CSSProperties = {
  height: '50vh',
  minHeight: '400px',
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const heroOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const heroContentStyle: React.CSSProperties = {
  textAlign: 'center',
  color: 'white',
  maxWidth: '900px',
  padding: '0 2rem',
  zIndex: 1,
};

// --- Compact hero (logged-in users) ---
const heroSectionCompactStyle: React.CSSProperties = {
  height: 'auto',
  minHeight: 'unset',
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const heroOverlayCompactStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem 0',
};

const heroContentCompactStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: 'white',
  width: '100%',
  maxWidth: '1400px',
  padding: '0 2rem',
  gap: '1.5rem',
  flexWrap: 'wrap',
};

const welcomeTitleStyle: React.CSSProperties = {
  fontSize: '1.1rem',
  fontWeight: '600',
  margin: 0,
  whiteSpace: 'nowrap',
};

const heroTitleStyle: React.CSSProperties = {
  fontSize: '3rem',
  fontWeight: 'bold',
  marginBottom: '0.75rem',
  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
};

const heroSubtitleStyle: React.CSSProperties = {
  fontSize: '1.3rem',
  marginBottom: '1rem',
  fontWeight: '300',
  textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
};

const heroDescriptionStyle: React.CSSProperties = {
  fontSize: '1rem',
  marginBottom: '1.5rem',
  lineHeight: '1.5',
  opacity: 0.9,
  textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
};

const heroButtonsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '1rem',
  justifyContent: 'center',
  flexWrap: 'wrap',
};

const primaryButtonStyle: React.CSSProperties = {
  backgroundColor: '#e74c3c',
  color: 'white',
  padding: '0.875rem 1.75rem',
  borderRadius: '8px',
  textDecoration: 'none',
  fontSize: '1rem',
  fontWeight: '600',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 15px rgba(231, 76, 60, 0.3)',
  border: '2px solid transparent',
  cursor: 'pointer',
};

const secondaryButtonStyle: React.CSSProperties = {
  backgroundColor: 'transparent',
  color: 'white',
  padding: '0.875rem 1.75rem',
  borderRadius: '8px',
  textDecoration: 'none',
  fontSize: '1rem',
  fontWeight: '600',
  transition: 'all 0.3s ease',
  border: '2px solid white',
  cursor: 'pointer',
};

const tertiaryButtonStyle: React.CSSProperties = {
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: 'white',
  padding: '0.875rem 1.75rem',
  borderRadius: '8px',
  textDecoration: 'none',
  fontSize: '1rem',
  fontWeight: '600',
  transition: 'all 0.3s ease',
  border: '2px solid rgba(255, 255, 255, 0.5)',
  cursor: 'pointer',
};

const matchesSectionStyle: React.CSSProperties = {
  flex: 1,
  backgroundColor: '#f8f9fa',
  padding: '0',
  overflow: 'auto',
};

const matchesContainerStyle: React.CSSProperties = {
  maxWidth: '100%',
  margin: '0 auto',
  height: '100%',
};

export default Home;
